import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff, Shield, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setLoading(false);
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/validate-reset-token/${token}`);
      if (res.ok) {
        const data = await res.json();
        setTokenValid(true);
        setUserEmail(data.user_email || '');
        setUserName(data.user_name || '');
      } else {
        setTokenValid(false);
      }
    } catch {
      setTokenValid(false);
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password-with-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      if (res.ok) {
        setSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to reset password');
      }
    } catch {
      toast.error('Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const hasLength = newPassword.length >= 8;
  const hasUpper = /[A-Z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Reset Password
          </h1>
        </div>

        {!token ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-6 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <p className="text-red-400 font-medium">Invalid Reset Link</p>
            <p className="text-white/60 text-sm mt-2">No reset token provided. Please use the link from your email.</p>
            <Button onClick={() => navigate('/login')} className="mt-4 bg-primary text-black" data-testid="go-to-login">
              Go to Login
            </Button>
          </div>
        ) : !tokenValid ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-6 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <p className="text-red-400 font-medium">Expired or Invalid Link</p>
            <p className="text-white/60 text-sm mt-2">This reset link has expired or has already been used. Please ask your administrator for a new one.</p>
            <Button onClick={() => navigate('/login')} className="mt-4 bg-primary text-black" data-testid="go-to-login">
              Go to Login
            </Button>
          </div>
        ) : success ? (
          <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-6 text-center">
            <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-3" />
            <p className="text-green-400 font-medium">Password Reset Successfully!</p>
            <p className="text-white/60 text-sm mt-2">You can now login with your new password.</p>
            <Button onClick={() => navigate('/login')} className="mt-4 bg-primary text-black" data-testid="go-to-login-success">
              Go to Login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 bg-black/40 border border-white/10 rounded-sm p-8">
            <div className="text-center mb-4">
              <p className="text-white/60 text-sm">
                Setting new password for <span className="text-white font-medium">{userEmail}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-white/80">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pl-10 pr-10 bg-black/50 border-white/10 text-white h-11"
                  required
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="space-y-1 text-xs">
                <p className={hasLength ? 'text-green-400' : 'text-white/30'}>At least 8 characters</p>
                <p className={hasUpper ? 'text-green-400' : 'text-white/30'}>Contains uppercase letter</p>
                <p className={hasNumber ? 'text-green-400' : 'text-white/30'}>Contains number</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/80">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pl-10 bg-black/50 border-white/10 text-white h-11"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              {confirmPassword && (
                <p className={passwordsMatch ? 'text-green-400 text-xs' : 'text-red-400 text-xs'}>
                  {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={submitting || !hasLength || !passwordsMatch}
              className="w-full bg-primary text-black hover:bg-primary/90 h-11 font-semibold"
              data-testid="reset-password-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Set New Password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
